import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger'

const logger = createLogger('CountyOnboarding')
const ctx = (extra) => adminLogContext('county_onboarding', extra)


const CountyOnboarding = () => {
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [selectedStateFips, setSelectedStateFips] = useState('');
  const [selectedCountyName, setSelectedCountyName] = useState('');
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Deletion state
  const [existingCounties, setExistingCounties] = useState([]);
  const [loadingExistingCounties, setLoadingExistingCounties] = useState(false);
  const [selectedDeleteCountyIds, setSelectedDeleteCountyIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingStates(true);
        setLoadingExistingCounties(true);
        setError('');
        
        const [statesResp, countiesResp] = await Promise.all([
          apiService.getTigerStates(),
          apiService.getAllCounties(),
        ]);
        
        setStates(statesResp.states || []);
        setExistingCounties(countiesResp.counties || []);
      } catch (err) {
        logger.apiFailure('Failed to load initial onboarding data', ctx({ action: 'load_initial_data' }), err);
        setError('Failed to load states or existing counties.');
      } finally {
        setLoadingStates(false);
        setLoadingExistingCounties(false);
      }
    };
    fetchInitialData();
  }, []);

  const handleStateChange = async (event) => {
    const fips = event.target.value;
    setSelectedStateFips(fips);
    setSelectedCountyName('');
    setCounties([]);
    if (!fips) return;
    try {
      setLoadingCounties(true);
      setError('');
      const resp = await apiService.getTigerCounties(fips);
      setCounties(resp.counties || []);
    } catch (err) {
      logger.apiFailure('Failed to load TIGER counties', ctx({
        action: 'load_tiger_counties',
        state_fips: fips,
      }), err);
      setError('Failed to load counties for selected state.');
    } finally {
      setLoadingCounties(false);
    }
  };

  const handleCreateCounty = async () => {
    if (!selectedStateFips || !selectedCountyName) return;
    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');

      const created = await apiService.createCountyFromTiger({
        state_fips: selectedStateFips,
        county_name: selectedCountyName,
      });

      setSuccessMessage(
        `Created county '${created.name}' with slug '${created.slug}'.`
      );
      
      // Refresh the existing counties list
      const countiesResp = await apiService.getAllCounties();
      setExistingCounties(countiesResp.counties || []);
    } catch (err) {
      logger.apiFailure('Failed to create county from TIGER', ctx({
        action: 'create_county_from_tiger',
        state_fips: selectedStateFips,
        county_name: selectedCountyName,
      }), err);
      setError(err?.message || 'Failed to create county from TIGER.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDeleteCounties = existingCounties.filter((c) =>
    selectedDeleteCountyIds.includes(c.id)
  );

  const handleOpenDeleteDialog = () => {
    if (selectedDeleteCountyIds.length === 0) return;
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmText('');
  };

  const handleDeleteCounty = async () => {
    if (selectedDeleteCountyIds.length === 0 || deleteConfirmText !== 'DELETE') return;
    try {
      setDeleting(true);
      setError('');
      setSuccessMessage('');
      handleCloseDeleteDialog();

      const ids = [...selectedDeleteCountyIds];
      let lastMessage = '';
      for (const countyId of ids) {
        const result = await apiService.deleteCounty(countyId);
        lastMessage = result?.message || lastMessage;
      }

      setSuccessMessage(
        ids.length === 1
          ? lastMessage || 'County deleted.'
          : `Successfully deleted ${ids.length} counties.`
      );
      setSelectedDeleteCountyIds([]);

      const countiesResp = await apiService.getAllCounties();
      setExistingCounties(countiesResp.counties || []);
    } catch (err) {
      logger.apiFailure('Failed to delete county', ctx({
        action: 'delete_county',
        county_ids: selectedDeleteCountyIds.map(String).join(','),
      }), err);
      setError(err?.message || 'Failed to delete one or more counties.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        County Onboarding (TIGER)
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Select a state and county from the TIGER shapefile to add it to
        LandSentry&apos;s known counties. This will create a new row in the
        <code> counties </code> table with a simplified boundary for the map.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="state-select-label">State</InputLabel>
            <Select
              labelId="state-select-label"
              label="State"
              value={selectedStateFips}
              onChange={handleStateChange}
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  sx: { zIndex: 9999 },
                },
              }}
            >
              {loadingStates && (
                <MenuItem value="">
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                </MenuItem>
              )}
              {!loadingStates &&
                states.map((s) => (
                  <MenuItem key={s.state_fips} value={s.state_fips}>
                    {s.state_code} ({s.state_fips})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{ minWidth: 260 }}
            disabled={!selectedStateFips || loadingCounties}
          >
            <InputLabel id="county-select-label">County</InputLabel>
            <Select
              labelId="county-select-label"
              label="County"
              value={selectedCountyName}
              onChange={(e) => setSelectedCountyName(e.target.value)}
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  sx: { zIndex: 9999 },
                },
              }}
            >
              {loadingCounties && (
                <MenuItem value="">
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                </MenuItem>
              )}
              {!loadingCounties &&
                counties.map((c) => (
                  <MenuItem key={c.name} value={c.name}>
                    {c.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              disabled={!selectedStateFips || !selectedCountyName || submitting}
              onClick={handleCreateCounty}
            >
              {submitting ? 'Creating…' : 'Add County'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Delete County Section */}
      <Typography variant="h5" gutterBottom color="error">
        Remove County
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Warning:</strong> This will permanently delete the selected
        counties and ALL associated data including documents, reports, scraping
        configurations, tasks, and user access records. This action cannot be undone.
      </Typography>

      <Paper sx={{ p: 2, borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 320, maxWidth: '100%' }}>
            <InputLabel id="delete-county-select-label">Select Counties to Remove</InputLabel>
            <Select
              labelId="delete-county-select-label"
              label="Select Counties to Remove"
              multiple
              value={selectedDeleteCountyIds}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedDeleteCountyIds(typeof v === 'string' ? v.split(',') : v);
              }}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) return '';
                if (selected.length <= 2) {
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const c = existingCounties.find((x) => x.id === id);
                        return c ? (
                          <Chip key={id} size="small" label={`${c.name} (${c.state})`} />
                        ) : null;
                      })}
                    </Box>
                  );
                }
                return `${selected.length} counties selected`;
              }}
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  sx: { zIndex: 9999, maxHeight: 360 },
                },
              }}
            >
              {loadingExistingCounties && (
                <MenuItem value="">
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                </MenuItem>
              )}
              {!loadingExistingCounties &&
                existingCounties.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name} ({c.state})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="error"
            disabled={selectedDeleteCountyIds.length === 0 || deleting}
            onClick={handleOpenDeleteDialog}
            startIcon={<DeleteForeverIcon />}
          >
            {deleting
              ? 'Deleting…'
              : selectedDeleteCountyIds.length > 1
                ? `Delete ${selectedDeleteCountyIds.length} Counties`
                : 'Delete County'}
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
        sx={{ zIndex: 10000 }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          {selectedDeleteCounties.length > 1 ? 'Confirm Counties Deletion' : 'Confirm County Deletion'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to permanently delete{' '}
            <strong>{selectedDeleteCounties.length}</strong>{' '}
            {selectedDeleteCounties.length === 1 ? 'county' : 'counties'} and ALL associated data
            for {selectedDeleteCounties.length === 1 ? 'it' : 'each'}:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2, maxHeight: 200, overflow: 'auto' }}>
            {selectedDeleteCounties.map((c) => (
              <li key={c.id}>
                <strong>
                  {c.name} ({c.state})
                </strong>
              </li>
            ))}
          </Box>
          <DialogContentText sx={{ mb: 2 }}>
            This includes:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, mb: 2, pl: 2 }}>
            <li>All uploaded documents and their files</li>
            <li>All generated reports</li>
            <li>All scraping configurations and tasks</li>
            <li>All municipality assignments</li>
            <li>
              All user access records for{' '}
              {selectedDeleteCounties.length === 1 ? 'this county' : 'these counties'}
            </li>
          </Box>
          <DialogContentText sx={{ mb: 2, fontWeight: 'bold', color: 'error.main' }}>
            This action cannot be undone!
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Type DELETE to confirm"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== 'DELETE'}
            helperText="Type DELETE in all caps to enable the delete button"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDeleteCounty}
            color="error"
            variant="contained"
            disabled={deleteConfirmText !== 'DELETE'}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CountyOnboarding;


